import { useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink, Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { API, errText } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { ErrorBox, Skel, ago } from "@/components/market/Shared";
import { PaperBetForm } from "./PaperBetForm";
import { OwnerRealBet } from "./OwnerRealBet";
import { cents, endsIn } from "./utils";
import { AiExplain } from "@/components/ai/AiExplain";

const Stat = ({ k, v }) => <div className="rounded-xl bg-white/[0.03] border border-[var(--line)] p-2.5"><div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div><div className="mt-1 font-num text-sm">{v}</div></div>;

const Body = ({ m }) => {
  const { owner } = useAccess();
  const p = m.data_pick;
  return (
    <div className="space-y-5 pb-10">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <Stat k={m.yes_name} v={cents(m.mid)} /><Stat k={m.no_name} v={m.mid == null ? "—" : cents(1 - m.mid)} />
        <Stat k="Spread" v={cents(m.spread)} /><Stat k={m.group === "sports" && m.game_start ? "Starts" : "Ends"} v={m.group === "sports" && m.game_start && new Date(m.game_start) <= new Date() ? "LIVE now" : endsIn(m)} />
      </div>
      <section data-testid="pm-explanation"><h3 className="font-head font-semibold text-sm mb-1">How this market works</h3>
        <p className="text-sm text-slate-300 leading-relaxed">{m.explanation}</p></section>
      <AiExplain slug={m.slug} />
      {p && <section className="rounded-2xl border border-[var(--violet)]/40 bg-[var(--violet)]/5 p-4" data-testid="pm-data-pick">
        <div className="flex items-center gap-2 text-sm font-semibold text-[var(--violet)]"><Sparkles className="w-4 h-4" />Data pick: {p.pick_name} ({Math.round(p.chance * 100)}%)</div>
        <ul className="mt-2 text-xs text-slate-300 space-y-1">{p.reasons.map((r) => <li key={r}>· {r}</li>)}</ul>
        <p className="mt-2 text-[11px] text-amber-200/80">{p.honest_note}</p></section>}
      <section><h3 className="font-head font-semibold text-sm mb-2">Practice bet</h3><PaperBetForm m={m} /></section>
      {owner && <OwnerRealBet m={m} />}
      {m.description && <section><h3 className="font-head font-semibold text-sm mb-1">Official rules</h3><p className="text-xs text-slate-400 leading-relaxed" data-testid="pm-rules">{m.description}</p></section>}
      <div className="flex items-center justify-between text-[11px] text-slate-500 font-num">
        <span>Live price · Polymarket US · {ago(m.quote?.fetched_at)}</span>
        <a href={m.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[var(--cyan)]" data-testid="pm-open-polymarket">Polymarket <ExternalLink className="w-3 h-3" /></a>
      </div>
    </div>
  );
};

export const MarketSheet = ({ slug, onClose }) => {
  const [m, setM] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!slug) return;
    setM(null); setErr(null);
    axios.get(`${API}/predict/market/${slug}`).then((r) => setM(r.data)).catch((e) => setErr(errText(e)));
  }, [slug]);
  return (
    <Sheet open={!!slug} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-[var(--bg)] border-[var(--line)]" data-testid="pm-sheet">
        <SheetHeader className="text-left mb-4">
          <SheetTitle className="font-head text-lg leading-snug pr-6 text-slate-100" data-testid="pm-sheet-title">{m?.label || "Loading market…"}</SheetTitle>
          <SheetDescription className="text-xs uppercase tracking-wider">{m?.group}{m?.sports_type ? ` · ${m.sports_type.replaceAll("_", " ")}` : ""}</SheetDescription>
        </SheetHeader>
        {err ? <ErrorBox msg={err} /> : !m ? <Skel h="h-80" /> : <Body m={m} />}
      </SheetContent>
    </Sheet>
  );
};
